import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationHeader from '../../components/ui/NavigationHeader';
import TrustSignals from '../../components/ui/TrustSignals';
import WalletSettingsPanel from './components/WalletSettingsPanel';
import NotificationSettings from './components/NotificationSettings';
import PrivacySettings from './components/PrivacySettings';
import AccountManagement from './components/AccountManagement';
import AdvancedSettings from './components/AdvancedSettings';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const Settings = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('wallet');
  const [settings, setSettings] = useState({
    wallet: {
      connectedAddress: '0x742d35Cc6634C0532925a3b8D4C9db96590b5b8c',
      networkPreference: 'polygon-mainnet',
      autoGasEstimation: true,
      customGasPrice: '25'
    },
    notifications: {
      transactionConfirmations: true,
      emailAlerts: true,
      browserNotifications: false,
      notarizationSuccess: true
    },
    privacy: {
      ipfsGateway: 'pinata',
      dataRetention: '1-year',
      archiveVisibility: 'private',
      analyticsOptOut: false
    },
    account: {
      exportFormat: 'json',
      backupReminders: true,
      connectedDApps: ['PolyNotary', 'MetaMask'],
      storageUsed: '245 MB'
    },
    advanced: {
      developerMode: false,
      customRPC: '',
      debugMode: false,
      experimentalFeatures: false
    }
  });

  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState({ isVisible: false, type: 'info', message: '' });

  const settingSections = [
    { id: 'wallet', label: 'Wallet', icon: 'Wallet', description: 'Manage wallet connections and gas settings' },
    { id: 'notifications', label: 'Notifications', icon: 'Bell', description: 'Configure alert preferences' },
    { id: 'privacy', label: 'Privacy', icon: 'Shield', description: 'Control data and visibility settings' },
    { id: 'account', label: 'Account', icon: 'User', description: 'Export data and manage connections' },
    { id: 'advanced', label: 'Advanced', icon: 'Settings', description: 'Developer and experimental options' }
  ];

  useEffect(() => {
    // Load settings from localStorage or API
    const savedSettings = localStorage.getItem('polynotary_settings');
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        setSettings(prevSettings => ({ ...prevSettings, ...parsedSettings }));
      } catch (error) {
        console.error('Failed to load saved settings:', error);
      }
    }
  }, []);

  const showToast = (type, message) => {
    setToast({ isVisible: true, type, message });
    setTimeout(() => {
      setToast({ isVisible: false, type: 'info', message: '' });
    }, 3000);
  };

  const handleSettingChange = (section, key, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev?.[section],
        [key]: value
      }
    }));
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Save to localStorage
      localStorage.setItem('polynotary_settings', JSON.stringify(settings));
      
      showToast('success', 'Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      showToast('error', 'Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetSection = () => {
    const defaultSettings = {
      wallet: {
        networkPreference: 'polygon-mainnet',
        autoGasEstimation: true,
        customGasPrice: '25'
      },
      notifications: {
        transactionConfirmations: true,
        emailAlerts: false,
        browserNotifications: false,
        notarizationSuccess: true
      },
      privacy: {
        ipfsGateway: 'pinata',
        dataRetention: '1-year',
        archiveVisibility: 'private',
        analyticsOptOut: false
      },
      account: {
        exportFormat: 'json',
        backupReminders: true
      },
      advanced: {
        developerMode: false,
        customRPC: '',
        debugMode: false,
        experimentalFeatures: false
      }
    };

    setSettings(prev => ({
      ...prev,
      [activeSection]: defaultSettings?.[activeSection]
    }));
    
    showToast('info', `${settingSections?.find(s => s?.id === activeSection)?.label} settings reset to default`);
  };

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'wallet':
        return (
          <WalletSettingsPanel
            settings={settings?.wallet}
            onChange={(key, value) => handleSettingChange('wallet', key, value)}
          />
        );
      case 'notifications':
        return (
          <NotificationSettings
            settings={settings?.notifications}
            onChange={(key, value) => handleSettingChange('notifications', key, value)}
          />
        );
      case 'privacy':
        return (
          <PrivacySettings
            settings={settings?.privacy}
            onChange={(key, value) => handleSettingChange('privacy', key, value)}
          />
        );
      case 'account':
        return (
          <AccountManagement
            settings={settings?.account}
            onChange={(key, value) => handleSettingChange('account', key, value)}
          />
        );
      case 'advanced':
        return (
          <AdvancedSettings
            settings={settings?.advanced}
            onChange={(key, value) => handleSettingChange('advanced', key, value)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationHeader />
      
      <main className="pt-16">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center space-x-3 mb-2">
                <Icon name="Settings" size={28} className="text-primary" />
                <h1 className="text-2xl font-bold text-foreground">Settings</h1>
              </div>
              <p className="text-muted-foreground">
                Configure your PolyNotary dApp preferences and account management
              </p>
            </div>

            {/* Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Left Sidebar - Navigation */}
              <div className="lg:col-span-1 space-y-4">
                {/* Settings Navigation */}
                <div className="bg-card border border-border rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-foreground mb-3">Settings Categories</h3>
                  <nav className="space-y-1">
                    {settingSections?.map((section) => (
                      <button
                        key={section?.id}
                        onClick={() => setActiveSection(section?.id)}
                        className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          activeSection === section?.id
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        }`}
                      >
                        <Icon name={section?.icon} size={16} />
                        <span>{section?.label}</span>
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Trust Signals - Desktop */}
                <div className="hidden lg:block">
                  <TrustSignals />
                </div>
              </div>

              {/* Main Content */}
              <div className="lg:col-span-3 space-y-6">
                {/* Section Header */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <Icon 
                        name={settingSections?.find(s => s?.id === activeSection)?.icon} 
                        size={24} 
                        className="text-primary" 
                      />
                      <div>
                        <h2 className="text-xl font-semibold text-foreground">
                          {settingSections?.find(s => s?.id === activeSection)?.label} Settings
                        </h2>
                        <p className="text-sm text-muted-foreground">
                          {settingSections?.find(s => s?.id === activeSection)?.description}
                        </p>
                      </div>
                    </div>
                    
                    <div className="hidden md:flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="RotateCcw"
                        onClick={handleResetSection}
                      >
                        Reset
                      </Button>
                      <Button
                        size="sm"
                        iconName="Save"
                        loading={isSaving}
                        onClick={handleSaveSettings}
                      >
                        Save Changes
                      </Button>
                    </div>
                  </div>

                  {/* Settings Content */}
                  <div className="border-t border-border pt-6">
                    {renderActiveSection()}
                  </div>
                </div>

                {/* Mobile Actions */}
                <div className="md:hidden bg-card border border-border rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      variant="outline"
                      fullWidth
                      iconName="RotateCcw"
                      onClick={handleResetSection}
                    >
                      Reset Section
                    </Button>
                    <Button
                      fullWidth
                      iconName="Save"
                      loading={isSaving}
                      onClick={handleSaveSettings}
                    >
                      Save Changes
                    </Button>
                  </div>
                </div>

                {/* Trust Signals - Mobile */}
                <div className="lg:hidden">
                  <TrustSignals />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Toast Notification */}
      {toast?.isVisible && (
        <div className="fixed bottom-4 right-4 z-50">
          <div className={`bg-card border border-border rounded-lg p-4 shadow-lg ${
            toast?.type === 'success' ? 'border-green-200' : 
            toast?.type === 'error' ? 'border-red-200' : 'border-blue-200'
          }`}>
            <div className="flex items-center space-x-2">
              <Icon 
                name={toast?.type === 'success' ? 'CheckCircle' : toast?.type === 'error' ? 'AlertCircle' : 'Info'} 
                size={16} 
                className={
                  toast?.type === 'success' ? 'text-green-600' : 
                  toast?.type === 'error' ? 'text-red-600' : 'text-blue-600'
                } 
              />
              <span className="text-sm font-medium text-foreground">{toast?.message}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;